import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import type { Role } from "@/api/auth";
import { useAuth } from "./AuthProvider";
import { Spinner } from "@/components/Spinner";

type Props = {
  children: ReactNode;
  /** Restrict to a single role. If omitted, any authenticated admin passes. */
  role?: Role;
  /** Skip the "must change password" gate. Used by the change-password page
   * itself so the user can actually complete the change. */
  allowDuringPasswordChange?: boolean;
};

export function RequireAuth({ children, role, allowDuringPasswordChange = false }: Props) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Checking session…" />
      </div>
    );
  }
  if (!me) {
    return <Navigate to="/admin/login" replace state={{ from: loc.pathname + loc.search }} />;
  }
  if (me.must_change_password && !allowDuringPasswordChange) {
    return <Navigate to="/admin/change-password" replace />;
  }
  if (role && me.role !== role) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}
